type AnswerWithImage = {
    answer_image?: unknown
    pending_images?: unknown[]
}

type AnswerWithMedia = {
    files?: unknown
    file_urls?: unknown
    recording?: unknown
    recording_url?: unknown
}

export type CompletedAnswerMedia = {
    files?: string[]
    recording?: string
}

export function mergeCompletedAnswerMediaForSubmit<T extends AnswerWithMedia>(
    answer: T,
    completedMedia?: CompletedAnswerMedia | null,
): T {
    if (!completedMedia?.recording && !Array.isArray(completedMedia?.files)) return answer

    return {
        ...answer,
        ...(Array.isArray(completedMedia.files) ? { files: completedMedia.files } : {}),
        ...(completedMedia.recording ? { recording: completedMedia.recording } : {}),
    }
}

export function hasPendingLocalMediaForSubmit(
    answer: AnswerWithMedia,
    completedMedia?: CompletedAnswerMedia | null,
): boolean {
    const prepared = mergeCompletedAnswerMediaForSubmit(answer, completedMedia)
    const hasPendingRecording = !!prepared.recording && typeof prepared.recording !== 'string'
    const firstFile = Array.isArray(prepared.files) ? prepared.files[0] : null
    const hasPendingFiles = !!firstFile && typeof firstFile !== 'string'
    const hasUploadedFiles =
        Array.isArray(prepared.files) &&
        prepared.files.some(file => typeof file === 'string' && file.length > 0)
    const hasLocalFilePreview =
        Array.isArray(answer.file_urls) &&
        answer.file_urls.some(file => {
            if (!file || typeof file !== 'object') return false
            const url = 'url' in file ? file.url : 'file_url' in file ? file.file_url : null
            return typeof url === 'string' && /^(blob|mediastream):/i.test(url)
        })

    return hasPendingRecording || hasPendingFiles || (hasLocalFilePreview && !hasUploadedFiles)
}

export function hasUnresolvedLocalVideoForSubmit(answer: AnswerWithMedia): boolean {
    if (answer.recording) return false
    return (
        typeof answer.recording_url === 'string' &&
        /^(blob|mediastream):/i.test(answer.recording_url)
    )
}

export async function prepareAnswerImageForSubmit<T extends AnswerWithImage>(
    answer: T,
    upload: (answer: T) => Promise<string | null>,
): Promise<T> {
    const signedId = await upload(answer)
    if (!signedId) return answer

    return {
        ...answer,
        answer_image: signedId,
    }
}

export async function prepareAnswerImagesForSubmit<T extends AnswerWithImage>(
    answer: T,
    upload: (answer: T) => Promise<string[]>,
): Promise<T> {
    const signedIds = await upload(answer)
    if (!signedIds.length) return answer

    return {
        ...answer,
        pending_images: signedIds,
        answer_image: signedIds[0],
    }
}
